// Static content: 7 services per FRD
export const SERVICES = [
    {
        slug: "product-engineering",
        title: "Product Engineering",
        tagline: "Ship enterprise-grade products with senior engineering pods.",
        icon: "Rocket",
        image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGVuZ2luZWVyaW5nJTIwdGVhbSUyMGNvbGxhYm9yYXRpbmclMjBvZmZpY2V8ZW58MHx8fHwxNzgyMDgxNTEyfDA&ixlib=rb-4.1.0&q=85",
        overview: "End-to-end product engineering for web, mobile, and platform products. Discovery, architecture, delivery, and hyper-care — with cross-functional pods that plug into your organization.",
        challenges: [
            "Ship faster without sacrificing quality or security posture.",
            "Extend engineering capacity without long hiring cycles.",
            "Modernize legacy applications while running the business.",
        ],
        approach: "Senior-first pods with embedded product ownership, TDD by default, trunk-based delivery, and outcome-based governance from day one.",
        capabilities: [
            "Product discovery & rapid prototyping",
            "Web & mobile app engineering",
            "API platforms and microservices",
            "Legacy modernization & strangler migrations",
            "Accessibility and design-system engineering",
            "Performance, resilience, and observability",
        ],
        stack: ["TypeScript", "React", "Next.js", "Node.js", "Python", "Go", "GraphQL", "PostgreSQL"],
        governance: "Weekly steering, transparent burndowns, defined DoR/DoD, and a single point of accountability from principal engineer to program manager.",
        engagements: ["Milestone-Based", "T&M", "Managed Product Pod"],
        faqs: [
            { q: "How senior are the engineers?", a: "Our pods lead with principal and senior engineers. Junior engineers are only added for supervised scale after core delivery velocity is established." },
            { q: "How fast can you spin up a pod?", a: "Typical spin-up is 10-14 business days for a 4-6 engineer pod, including on-boarding to your tooling and security controls." },
            { q: "Do you sign IP and NDA agreements?", a: "Yes — mutual NDA and IP assignment are standard on every engagement." },
        ],
    },
    {
        slug: "cloud-devops",
        title: "Cloud & DevOps",
        tagline: "Reliable cloud platforms, disciplined FinOps, and paved-road DevOps.",
        icon: "Cloud",
        image: "https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg",
        overview: "Cloud-native platform engineering across AWS, Azure, and GCP. We build the paved road: golden path CI/CD, secure landing zones, and cost-aware delivery.",
        challenges: [
            "Uncontrolled cloud spend eroding operating margin.",
            "Fragile release pipelines with long incident recovery.",
            "Compliance readiness for HIPAA, SOC 2, PCI-DSS.",
        ],
        approach: "Platform-as-a-product. We build internal developer platforms with self-service golden paths, guardrails, and cost visibility baked into the workflow.",
        capabilities: [
            "Landing zone & multi-account architecture",
            "Kubernetes platform engineering",
            "Golden-path CI/CD & GitOps",
            "Site reliability & observability",
            "FinOps guardrails & rightsizing",
            "Cloud migration & modernization",
        ],
        stack: ["AWS", "Azure", "GCP", "Kubernetes", "Terraform", "ArgoCD", "Datadog", "OpenTelemetry"],
        governance: "SRE-aligned SLOs, defined error budgets, blameless post-mortems, and monthly FinOps reviews with unit-economics reporting.",
        engagements: ["Milestone-Based", "T&M", "Managed Platform"],
        faqs: [
            { q: "Which cloud do you specialize in?", a: "We are certified across AWS, Azure, and GCP with deep specializations in each." },
            { q: "How much cost can I expect to save?", a: "Sustained 20-35% cloud cost reduction within two quarters is typical across our engagements." },
        ],
    },
    {
        slug: "data-ai",
        title: "Data & AI",
        tagline: "From data platforms to production GenAI — outcomes, not experiments.",
        icon: "BrainCircuit",
        image: "https://images.unsplash.com/photo-1767416567191-baff01fc81ff",
        overview: "We build modern data platforms and put GenAI into production. Our teams combine data engineering rigor with applied ML and evaluation discipline.",
        challenges: [
            "GenAI pilots that never reach production.",
            "Fragmented data landscape blocking analytics velocity.",
            "Lack of governance for AI model risk.",
        ],
        approach: "Layered blueprint: pluggable storage (Iceberg/Delta), warehouse-agnostic transformation (dbt), governance as code, and evaluation-first ML delivery.",
        capabilities: [
            "Modern data platforms (lakehouse, warehouse)",
            "MLOps & model risk governance",
            "GenAI workflow engineering",
            "RAG & agentic architectures",
            "Streaming data & real-time analytics",
            "Data quality & observability",
        ],
        stack: ["Databricks", "Snowflake", "dbt", "Airflow", "PyTorch", "LangGraph", "Pinecone", "Iceberg"],
        governance: "AI risk framework, evaluation harnesses, drift monitoring, and human-in-the-loop review gates for regulated workloads.",
        engagements: ["Milestone-Based", "T&M", "AI Center of Excellence"],
        faqs: [
            { q: "Can you help us get a GenAI product to production?", a: "Yes — most of our GenAI engagements are production-bound with defined evaluation KPIs from week one." },
        ],
    },
    {
        slug: "analytics",
        title: "Analytics",
        tagline: "Decision-grade analytics with unit economics, not vanity dashboards.",
        icon: "BarChart3",
        image: "https://images.pexels.com/photos/6804071/pexels-photo-6804071.jpeg",
        overview: "Analytics engineering, business intelligence, and decision science tuned to operating rhythms — not just quarterly reviews.",
        challenges: [
            "Dashboards that no one uses and everyone distrusts.",
            "Disconnected KPIs across finance, product, and ops.",
            "Slow, expensive analytics driven by ad-hoc SQL.",
        ],
        approach: "Metric layer as source of truth, semantic model over your warehouse, and a delivery cadence that keeps analytics adjacent to the business.",
        capabilities: [
            "Metric layer & semantic modeling",
            "Executive & operational BI",
            "Customer analytics & segmentation",
            "Marketing & revenue analytics",
            "Product analytics & experimentation",
        ],
        stack: ["dbt", "Looker", "Tableau", "PowerBI", "Snowflake", "BigQuery"],
        governance: "Metric ownership matrix, quarterly certification, and change control for critical business definitions.",
        engagements: ["Milestone-Based", "T&M", "Managed Analytics"],
        faqs: [
            { q: "Do you work with existing BI stacks?", a: "Yes — we augment and modernize existing Looker/Tableau/PowerBI stacks rather than pushing a wholesale replacement." },
        ],
    },
    {
        slug: "testing",
        title: "Quality Engineering",
        tagline: "Shift-left testing and continuous quality signals.",
        icon: "ShieldCheck",
        image: "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg",
        overview: "Modern quality engineering across web, mobile, API, and data pipelines. Shift-left automation, non-functional coverage, and QE-as-a-service.",
        challenges: [
            "Regression backlog eating release velocity.",
            "No visibility into non-functional risk.",
            "Fragile test suites and flaky CI.",
        ],
        approach: "Test pyramid discipline, contract-first API testing, and observability-driven QA. We treat quality signals as first-class production telemetry.",
        capabilities: [
            "Test automation strategy",
            "API & contract testing",
            "Performance & load testing",
            "Security & DAST integration",
            "Accessibility testing",
            "QE-as-a-service pods",
        ],
        stack: ["Playwright", "Cypress", "k6", "JMeter", "Pact", "Postman", "Axe"],
        governance: "Weekly quality report with escaped-defect trends, MTTR, and non-functional coverage by risk area.",
        engagements: ["Milestone-Based", "T&M", "Managed QE Pod"],
        faqs: [
            { q: "Can you rescue a stalled test automation program?", a: "Yes — we run 4-week diagnostics to stabilize CI signals before layering new coverage." },
        ],
    },
    {
        slug: "intelligent-automation",
        title: "Intelligent Automation",
        tagline: "Process automation with the intelligence layer built in.",
        icon: "Workflow",
        image: "https://images.pexels.com/photos/12935042/pexels-photo-12935042.jpeg",
        overview: "RPA, workflow automation, and document intelligence — orchestrated with modern AI so automations are resilient, not brittle.",
        challenges: [
            "Legacy RPA bots breaking under UI change.",
            "Document-heavy processes bottlenecking finance and ops.",
            "Fragmented workflows across ERPs and SaaS tools.",
        ],
        approach: "API-first where possible, UI automation only when necessary, with LLM-based document understanding and human-in-the-loop escalation.",
        capabilities: [
            "Document intelligence (IDP)",
            "Workflow automation",
            "RPA modernization",
            "Agentic process automation",
            "Integration & orchestration",
        ],
        stack: ["UiPath", "Camunda", "Temporal", "LangGraph", "n8n", "Zapier"],
        governance: "Automation catalog, ROI tracking per process, and quarterly automation review board.",
        engagements: ["Milestone-Based", "T&M", "Automation CoE"],
        faqs: [
            { q: "How do you decide between RPA and API integration?", a: "We default to API integration wherever the target system supports it, and use RPA only when APIs are absent or economically prohibitive." },
        ],
    },
    {
        slug: "security",
        title: "Security",
        tagline: "Security engineering integrated into every stage of delivery.",
        icon: "Lock",
        image: "https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg",
        overview: "Application security, cloud security, and compliance engineering — delivered as a first-class engineering discipline, not a checkbox.",
        challenges: [
            "Compliance readiness for SOC 2, HIPAA, PCI, ISO 27001.",
            "Application security debt slowing releases.",
            "Fragmented identity and secrets management.",
        ],
        approach: "Shift-left security with paved-road tooling: SAST/DAST/SCA in CI, threat modeling for critical flows, and continuous compliance evidence.",
        capabilities: [
            "AppSec & secure SDLC",
            "Cloud security posture management",
            "Identity & access engineering",
            "Compliance readiness (SOC 2, HIPAA, PCI)",
            "Threat modeling & red team support",
            "Secrets & key management",
        ],
        stack: ["Snyk", "Semgrep", "Wiz", "Prisma Cloud", "HashiCorp Vault", "Okta"],
        governance: "Continuous compliance dashboard, quarterly risk reviews, and incident response playbooks integrated with your SOC.",
        engagements: ["Milestone-Based", "T&M", "vCISO"],
        faqs: [
            { q: "Do you support SOC 2 readiness?", a: "Yes — we deliver evidence collection, control mapping, and audit support for SOC 2 Type I and Type II." },
        ],
    },
];

export function getServiceBySlug(slug) {
    return SERVICES.find((s) => s.slug === slug);
}
